import React from 'react';
import { formatDateOnly } from '../utils/format';

/**
 * Cases.
 *
 * "Cases" is the label; the data underneath is unchanged. Filevine project
 * ids and the exact source phase values are shown as Filevine sends them —
 * the approved mockup split a phase into a track and a remainder across two
 * lines, which reads well for the two Settled-Final Steps phases and mangles
 * every other one, so the whole value is printed.
 *
 * Nothing about search, filtering, paging or how a case opens has changed.
 */

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
       strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" />
  </svg>
);

const ProjectsList = ({
  projects,
  pagination,
  pageSize,
  searchTerm,
  setSearchTerm,
  phaseFilter,
  setPhaseFilter,
  allowedPhases,
  currentPage,
  setCurrentPage,
  setSelectedProject,
  loading,
  onRefresh,
  refreshing,
}) => {
  const projectsArray = Array.isArray(projects) ? projects : [];
  const totalPages = Math.ceil(pagination.total / pageSize);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const first = ((currentPage - 1) * pageSize) + 1;
  const last = Math.min(currentPage * pageSize, pagination.total);

  return (
    <>
      <div className="lm-eyebrow">Your case portfolio</div>
      <h1>Cases</h1>
      <p className="lm-subtitle">Financials, providers, and the next step for every client.</p>

      <div className="lm-toolbar">
        <label className="lm-search">
          <SearchIcon />
          <input type="text" placeholder="Search by client name…"
                 aria-label="Search cases by client name"
                 value={searchTerm} onChange={handleSearchChange} />
        </label>
        <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}
                aria-label="Filter by phase">
          {Array.isArray(allowedPhases) && allowedPhases.map((phase) => (
            <option key={phase} value={phase}>{phase}</option>
          ))}
        </select>
        {onRefresh && (
          <button type="button" className="lm-button" onClick={onRefresh} disabled={refreshing}
                  title="Refresh the case list from Filevine (use this if a new case isn't showing up)">
            {refreshing ? 'Refreshing…' : '↻ Refresh from Filevine'}
          </button>
        )}
      </div>

      <section className="lm-panel">
        <div className="lm-rowhead">
          <h2>
            Cases in scope
            {pagination.total > 0 && <span className="lm-count">{pagination.total}</span>}
          </h2>
        </div>

        {loading ? (
          <div className="lm-loading">Loading cases…</div>
        ) : projectsArray.length === 0 ? (
          <div className="lm-empty">
            {searchTerm || phaseFilter
              ? 'No cases match that search.'
              : 'No cases found.'}
          </div>
        ) : (
          <>
            <div className="lm-tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>Client and case</th>
                    <th>Incident</th>
                    <th>Phase</th>
                    <th aria-label="Open" />
                  </tr>
                </thead>
                <tbody>
                  {projectsArray.map((project) => (
                    <tr key={project.id || project.filevine_project_id}
                        onClick={() => setSelectedProject(project)}
                        style={{ cursor: 'pointer' }}>
                      <td>
                        <strong>{project.client_name || 'Unknown'}</strong>
                        <small>Case {project.case_id || project.id || 'N/A'}</small>
                      </td>
                      <td>{formatDateOnly(project.incident_date)}</td>
                      <td>{project.phase_name || 'N/A'}</td>
                      <td className="lm-num">
                        <button type="button" className="lm-link"
                                onClick={(e) => { e.stopPropagation(); setSelectedProject(project); }}>
                          Open →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="lm-bottomline">
              <span>
                Showing {first} to {last} of {pagination.total} case
                {pagination.total === 1 ? '' : 's'}
              </span>
              <span className="lm-pager">
                <button type="button" className="lm-button"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}>
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button key={pageNum} type="button"
                            className={`lm-button lm-page${currentPage === pageNum ? ' lm-primary' : ''}`}
                            aria-current={currentPage === pageNum ? 'page' : undefined}
                            onClick={() => handlePageChange(pageNum)}>
                      {pageNum}
                    </button>
                  );
                })}
                <button type="button" className="lm-button"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages}>
                  Next
                </button>
              </span>
            </div>
          </>
        )}
      </section>
    </>
  );
};

export default ProjectsList;
